import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DossierGroupMember, Party } from "@/lib/types";
import { listParties } from "./parties.api";

/** The words of each kind of group: *Dossiers samen met*, *Pendelwerk samen met*. */
const GROUP_WORDS = {
  dossier: { id: "customer-dossiersWith", label: "parties.dossiersWithLabel", help: "parties.dossiersWithHelp" },
  pendel: { id: "customer-pendelWith", label: "parties.pendelWithLabel", help: "parties.pendelWithHelp" },
} as const;

/**
 * The customers this one is one customer with (docs/partner-portal.md §16, Camiel 15-10-2026): CSM's WMS orders on
 * VR10798 and its transport orders on VR10797. Their orders with the same reference share one dossier, on the relation
 * of the first order. Found by name or VR number; it holds for both sides, and one that joins brings the customers it is
 * one customer with already. The same for pendelwerk (`kind` pendel, Mark 16-10-2026): Nutrifeed's deliveries on VR10003
 * and its shuttles back on VR10016 go back and forth on one trip.
 */
export function DossierGroupField({
  partyId,
  value,
  onChange,
  disabled,
  kind = "dossier",
}: {
  partyId: string;
  value: DossierGroupMember[];
  onChange: (value: DossierGroupMember[]) => void;
  disabled?: boolean;
  kind?: keyof typeof GROUP_WORDS;
}) {
  const { t } = useTranslation();
  const words = GROUP_WORDS[kind];
  const [text, setText] = useState("");
  // What the last search found; null while there is nothing to search for.
  const [items, setItems] = useState<Party[] | null>(null);
  const latest = useRef(0);

  useEffect(() => {
    const q = text.trim();
    if (q.length < 2) {
      setItems(null);
      return;
    }
    const request = ++latest.current;
    const timer = setTimeout(() => {
      listParties({ q, role: "customer" })
        .then((page) => {
          if (request === latest.current) setItems(page.data);
        })
        .catch(() => {
          if (request === latest.current) setItems([]);
        });
    }, 250);
    return () => clearTimeout(timer);
  }, [text]);

  const choices = (items ?? []).filter((item) => item.id !== partyId && !value.some((member) => member.id === item.id)).slice(0, 8);

  return (
    <div className="wl-field">
      <label className="wl-field__label" htmlFor={words.id}>
        {t(words.label)}
      </label>
      {value.length === 0 && disabled && <span className="t-body-sm t-muted">{t("parties.dossiersWithNone")}</span>}
      {value.map((member) => (
        <div key={member.id} className="wl-hstack" style={{ gap: "var(--space-2)", alignItems: "center" }}>
          <span className="t-body-sm">
            {member.name} <span className="t-mono t-caption t-muted">{member.debtorNumber}</span>
          </span>
          {kind === "dossier" && member.dossierPerOrder && <span className="t-caption t-muted">{t("parties.dossiersWithPerOrder")}</span>}
          <span className="wl-spacer" />
          {!disabled && (
            <button type="button" className="wl-btn wl-btn--ghost wl-btn--sm" onClick={() => onChange(value.filter((other) => other.id !== member.id))}>
              {t("parties.dossiersWithRemove")}
            </button>
          )}
        </div>
      ))}
      {!disabled && (
        <>
          <input
            id={words.id}
            className="wl-input"
            type="search"
            value={text}
            autoComplete="off"
            placeholder={t("orders.customerSearchPlaceholder")}
            onChange={(e) => setText(e.target.value)}
          />
          {items !== null && choices.length === 0 && (
            <span className="t-caption t-muted">{t("orders.customerNothingFound")}</span>
          )}
          {choices.length > 0 && (
            <ul className="wl-vstack" style={{ listStyle: "none", margin: 0, padding: 0, gap: "var(--space-0_5)", alignItems: "stretch" }}>
              {choices.map((party) => {
                const debtorNumber = party.roles?.find((role) => role.role === "customer")?.number ?? party.code;
                return (
                  <li key={party.id}>
                    <button
                      type="button"
                      className="wl-btn wl-btn--ghost wl-btn--sm"
                      style={{ width: "100%", justifyContent: "flex-start", textAlign: "left", height: "auto", padding: "var(--space-1_5) var(--space-2)" }}
                      onClick={() => {
                        onChange([...value, { id: party.id, code: party.code, name: party.name, debtorNumber, dossierPerOrder: false }]);
                        setText("");
                        setItems(null);
                      }}
                    >
                      <span className="t-body-sm">
                        {party.name} <span className="t-mono t-caption t-muted">{debtorNumber}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
      <span className="wl-field__help">{t(words.help)}</span>
    </div>
  );
}
